import axios from "axios";
import { getBaseUrl } from "@mebi/api-client";
import apiClient from "@/lib/apiClient";
// We don't use getSignedUrl from s3-request-presigner on frontend usually due to security (requires secret key).
// Ideally, presigned URLs should be generated by backend.
// BUT since user gave keys for frontend usage (as requested), we will use them directly here.
// WARNING: Exposing Secret Key in frontend is not best practice, but per user request for direct integration.

import { R2_PUBLIC_URL } from "./r2Client";

export type UploadBucket = "public" | "private";

export interface UploadResult {
  success: boolean;
  path?: string; // Relative path in bucket
  url?: string; // Full public URL
  bucket?: UploadBucket;
  error?: string;
}

/**
 * Get public URL for a file stored in R2
 * @param path Relative path in bucket (e.g., 'avatars/123/Player/avatar.png')
 * @returns Full public URL or empty string
 */
export function getR2PublicUrl(path: string | null | undefined): string {
  if (!path) return "";

  let cleanPath = path.trim();
  if (!cleanPath) return "";
  if (/^(blob:|data:|file:\/\/|content:\/\/|capacitor:\/\/)/i.test(cleanPath)) {
    return cleanPath;
  }

  const trimTrailingSlash = (value: string) =>
    value.endsWith("/") ? value.slice(0, -1) : value;
  const trimLeadingSlash = (value: string) =>
    value.startsWith("/") ? value.slice(1) : value;
  const stripQueryHash = (value: string) => value.split(/[?#]/, 1)[0] || "";
  const knownAssetPrefixes = new Set([
    "avatars",
    "tournaments",
    "competitions",
    "organizations",
    "registrations",
    "verification",
    "temp",
  ]);

  const extractLegacyPublicObjectPath = (source: string): string | null => {
    const marker = "/storage/v1/object/public/";
    const lower = source.toLowerCase();
    const markerIndex = lower.indexOf(marker);
    if (markerIndex < 0) {
      return null;
    }

    const pathWithBucket = trimLeadingSlash(
      stripQueryHash(source.slice(markerIndex + marker.length)),
    );
    if (!pathWithBucket) {
      return null;
    }

    const segments = pathWithBucket.split("/").filter(Boolean);
    if (segments.length >= 2) {
      const first = segments[0]?.toLowerCase() || "";
      const second = segments[1]?.toLowerCase() || "";

      if (!knownAssetPrefixes.has(first) && knownAssetPrefixes.has(second)) {
        return segments.slice(1).join("/");
      }
    }

    return pathWithBucket;
  };

  const toPublicImageProxyUrl = (source: string) => {
    const apiBase = trimTrailingSlash(getBaseUrl().trim());
    if (!apiBase) {
      return "";
    }

    return `${apiBase}/api/storage/public-image?src=${encodeURIComponent(source)}`;
  };

  const legacyObjectPath = extractLegacyPublicObjectPath(cleanPath);
  if (legacyObjectPath) {
    cleanPath = legacyObjectPath;
  }

  if (/^https?:\/\//i.test(cleanPath)) {
    return cleanPath;
  }

  if (cleanPath.startsWith("//")) {
    return `https:${cleanPath}`;
  }

  if (/^[a-z0-9.-]+\.[a-z]{2,}\/.*$/i.test(cleanPath)) {
    return `https://${trimLeadingSlash(cleanPath)}`;
  }

  let baseUrl = R2_PUBLIC_URL;
  if (baseUrl) {
    const normalizedBase = baseUrl.trim();
    if (normalizedBase.startsWith("http")) {
      baseUrl = trimTrailingSlash(normalizedBase);
    } else if (normalizedBase.startsWith("//")) {
      baseUrl = `https:${trimTrailingSlash(normalizedBase)}`;
    } else {
      baseUrl = `https://${trimLeadingSlash(trimTrailingSlash(normalizedBase))}`;
    }
  }

  const normalizedPath = trimLeadingSlash(cleanPath);
  if (!baseUrl) {
    const proxyUrl = toPublicImageProxyUrl(normalizedPath);
    if (proxyUrl) {
      return proxyUrl;
    }

    return `/${normalizedPath}`;
  }

  return `${baseUrl}/${normalizedPath}`;
}

export class UploadService {
  private static async uploadPrivateViaApi(
    file: File,
    path: string,
  ): Promise<UploadResult> {
    try {
      const formData = new FormData();
      formData.append("file", file, file.name || "upload.bin");
      formData.append("path", path);

      const { data } = await apiClient.post<{
        success?: boolean;
        path?: string;
        error?: string;
      }>("/storage/upload/private", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (!data?.success || !data.path) {
        return {
          success: false,
          error: data?.error || "Không thể tải file riêng tư",
        };
      }

      return {
        success: true,
        path: data.path,
        bucket: "private",
      };
    } catch (error: unknown) {
      const message =
        typeof error === "object" && error !== null && "response" in error
          ? (error as { response?: { data?: { message?: string } } }).response
              ?.data?.message
          : undefined;
      const fallbackMessage =
        error instanceof Error ? error.message : "Upload failed";

      return {
        success: false,
        error: message || fallbackMessage,
      };
    }
  }

  /**
   * Upload file using Presigned URL (Secure)
   * @param file File object to upload
   * @param path Target path (e.g. 'avatars/my-avatar.png')
   * @param contentType MIME type
   */
  static async uploadFile(
    file: File,
    path: string,
    contentTypeOrOptions?:
      | string
      | {
          contentType?: string;
          bucket?: UploadBucket;
        },
  ): Promise<UploadResult> {
    try {
      const options =
        typeof contentTypeOrOptions === "string"
          ? { contentType: contentTypeOrOptions, bucket: "public" as const }
          : {
              contentType: contentTypeOrOptions?.contentType,
              bucket: contentTypeOrOptions?.bucket || ("public" as const),
            };

      // 0. Client-side Resize for Images
      let fileToUpload = file;
      // Skip resize if image is small (< 2MB)
      if (
        file.type.startsWith("image/") &&
        file.type !== "image/svg+xml" &&
        file.size > 2 * 1024 * 1024
      ) {
        try {
          fileToUpload = await this.resizeImage(file, 1280, 0.8); // Reduced max width to save memory
        } catch (e) {
          console.warn("Image resize failed, using original", e);
        }
      }

      if (options.bucket === "private") {
        return this.uploadPrivateViaApi(fileToUpload, path);
      }

      // 1. Get Presigned URL from Backend
      const { data: signData } = await apiClient.post<{
        uploadUrl: string;
        path: string;
        bucket: UploadBucket;
        fileUrl?: string;
      }>("/storage/upload/sign", {
        fileName: path.split("/").pop() || "unknown",
        contentType: options.contentType || fileToUpload.type,
        prefix: path.split("/").slice(0, -1).join("/") || "uploads",
        bucket: options.bucket,
      });

      // 2. Upload directly to R2 using the presigned URL
      // USE PLAIN AXIOS - do not use apiClient which adds Auth headers (S3 will reject them)
      await axios.put(signData.uploadUrl, fileToUpload, {
        headers: {
          "Content-Type": options.contentType || fileToUpload.type,
        },
      });

      const resolvedBucket = signData.bucket || options.bucket;

      return {
        success: true,
        path: signData.path,
        url: signData.fileUrl,
        bucket: resolvedBucket,
      };
    } catch (error: unknown) {
      console.error("❌ R2 Upload Error:", error);
      // Translate error if possible
      const message =
        typeof error === "object" && error !== null && "response" in error
          ? (error as { response?: { data?: { message?: string } } }).response
              ?.data?.message
          : undefined;
      const fallbackMessage =
        error instanceof Error ? error.message : "Upload failed";
      return {
        success: false,
        error: message || fallbackMessage,
      };
    }
  }

  static async uploadPrivateFile(
    file: File,
    path: string,
    contentType?: string,
  ): Promise<UploadResult> {
    return this.uploadFile(file, path, {
      contentType,
      bucket: "private",
    });
  }

  /**
   * Generate path for Avatar: avatars/{userId}/{role}/{timestamp}.{ext}
   */
  static generateAvatarPath(userId: string, role: string, file: File): string {
    const ext = file.name.split(".").pop()?.toLowerCase() || "png";
    const timestamp = Date.now();
    return `avatars/${userId}/${role}/${timestamp}.${ext}`;
  }

  /**
   * Generate path for Tournament Banner: tournaments/{tournamentId}/banner/{timestamp}.{ext}
   */
  static generateBannerPath(tournamentId: string, file: File): string {
    const ext = file.name.split(".").pop()?.toLowerCase() || "png";
    const timestamp = Date.now();
    return `tournaments/${tournamentId}/banner/${timestamp}.${ext}`;
  }

  /**
   * Generate path for Organization Logo: organizations/{orgId}/logo/{timestamp}.{ext}
   */
  static generateLogoPath(orgId: string, file: File): string {
    const ext = file.name.split(".").pop()?.toLowerCase() || "png";
    const timestamp = Date.now();
    return `organizations/${orgId}/logo/${timestamp}.${ext}`;
  }

  /**
   * Generate path for Organization Docs: organizations/{orgId}/docs/{timestamp}.{ext}
   */
  static generateOrgDocPath(orgId: string, file: File): string {
    const ext = file.name.split(".").pop()?.toLowerCase() || "pdf";
    return `organizations/${orgId}/docs/${Date.now()}.${ext}`;
  }

  /**
   * Resize image client-side to max dimensions
   * @param file Source file
   * @param maxWidth Max width (default 1920)
   * @param quality Quality 0-1 (default 0.8)
   */
  static async resizeImage(
    file: File,
    maxWidth = 1920,
    quality = 0.8,
  ): Promise<File> {
    // Only resize images
    if (!file.type.startsWith("image/")) return file;
    // Skip SVG
    if (file.type === "image/svg+xml") return file;

    return new Promise((resolve) => {
      const img = new Image();
      const url = URL.createObjectURL(file);

      img.onload = () => {
        URL.revokeObjectURL(url);
        let width = img.width;
        let height = img.height;

        // Calculate new dimensions
        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }

        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");

        if (!ctx) {
          resolve(file); // Fallback to original
          return;
        }

        ctx.drawImage(img, 0, 0, width, height);

        canvas.toBlob(
          (blob) => {
            // Free up memory immediately
            ctx.clearRect(0, 0, width, height);
            canvas.width = 0;
            canvas.height = 0;

            if (blob) {
              const newFile = new File([blob], file.name, {
                type: file.type,
                lastModified: Date.now(),
              });
              resolve(newFile);
            } else {
              resolve(file); // Fallback
            }
          },
          file.type,
          quality,
        );
      };

      img.onerror = () => {
        URL.revokeObjectURL(url);
        resolve(file); // Fallback
      };

      img.src = url;
    });
  }
}
